import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { confirmDestructive } from '../../ui/dialogs';
import { useAuth } from '../../contexts/AuthContext';
import { useCollection } from '../../runtime/mobile/useCollection';
import { createAdminClient } from '../../runtime/mobile/adminClient';
import { readableMobileError } from '../../runtime/mobile/employeeRuntimeState';
import { colors, radius, spacing, typography } from '../../config/theme';
import { Card, Chip, ErrorBanner, Loader, SectionLabel, TextButton, screen } from '../ui';
import TaskEditorSheet from './TaskEditorSheet';

// ASCII only — ids ride in an idempotency HTTP header (no Cyrillic allowed).
const slug = (text) => (text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/(^-|-$)/g, '').slice(0, 30) || 'x';
const timingLabel = (timing) => {
  const av = timing?.available;
  const du = timing?.due;
  if (av?.basis === 'absolute_local_time') {
    return du?.localTime ? `${av.localTime}–${du.localTime}` : `с ${av.localTime}`;
  }
  const word = av?.basis === 'shift_end' ? 'конец' : 'старт';
  const sign = (n) => (n > 0 ? `+${n}` : `${n}`);
  const a = av?.offsetMinutes ?? 0;
  return du?.offsetMinutes != null ? `${word} ${sign(a)} / до ${sign(du.offsetMinutes)}` : `${word} ${sign(a)} / без срока`;
};

export default function ChecklistEditorScreen({ route, navigation }) {
  const { id } = route.params;
  const { user } = useAuth();
  const checklists = useCollection('checklistTemplates');
  const taskTemplates = useCollection('taskTemplates');
  const client = useMemo(() => createAdminClient(user), [user]);

  const [editing, setEditing] = useState(null); // task doc or {} for "new"
  const [error, setError] = useState(null);

  const checklist = checklists.docs.find((c) => c.id === id);
  const tasks = useMemo(() => {
    if (!checklist) return [];
    const byId = {};
    for (const t of taskTemplates.docs) byId[t.id] = t;
    return (checklist.taskTemplateIds || []).map((tid) => byId[tid]).filter(Boolean);
  }, [checklist, taskTemplates.docs]);

  if (checklists.loading && !checklist) return <Loader />;
  if (!checklist) return (
    <View style={screen.root}><Text style={styles.gone}>Чек-лист не найден</Text></View>
  );

  const addTask = async (payload) => {
    const taskId = `task-${slug(payload.title)}-${Date.now().toString(36)}`;
    await client.addTask(checklist.id, taskId, payload);
  };
  const updateTask = async (payload) => {
    await client.updateTask(editing.id, payload);
  };
  const removeTask = (task) => {
    confirmDestructive(
      'Удалить задачу?',
      `«${task.title}» будет отвязана от чек-листа. История прошлых смен сохранится.`,
      'Удалить',
      async () => {
        try { await client.removeTask(task.id); } catch (e) { setError(readableMobileError(e)); }
      },
    );
  };

  return (
    <View style={screen.root}>
      <ScrollView contentContainerStyle={screen.content}>
        <ErrorBanner message={error || checklists.error} />
        <View style={styles.head}>
          <View style={{ flex: 1 }}>
            <Text style={styles.name}>{checklist.name}</Text>
            <Text style={styles.meta}>v{checklist.templateVersion} · применяется к новым сменам</Text>
          </View>
        </View>

        <SectionLabel>Задачи</SectionLabel>
        {tasks.map((t) => (
          <Card key={t.id} onPress={() => setEditing(t)} style={styles.task}>
            <View style={styles.taskTop}>
              <Text style={styles.taskTitle}>{t.title}</Text>
              <Text style={styles.taskTime}>{timingLabel(t.timing)}</Text>
            </View>
            <View style={styles.chips}>
              {t.blocksShiftClosing && <Chip label="блокирует закрытие" kind="block" />}
              {t.requirements?.photoRequired && <Chip label="фото" />}
              {t.requirements?.amountRequired && <Chip label="сумма" />}
              {t.requirements?.commentRequired && <Chip label="комментарий" />}
            </View>
            <View style={styles.taskActions}>
              <TextButton title="Изменить" onPress={() => setEditing(t)} />
              <TextButton title="Удалить" kind="danger" onPress={() => removeTask(t)} />
            </View>
          </Card>
        ))}
        {!tasks.length && <Text style={styles.empty}>В наборе пока нет задач</Text>}

        <Pressable style={styles.addTask} onPress={() => setEditing({})}>
          <Text style={styles.addPlus}>+</Text>
          <Text style={styles.addText}>Добавить задачу</Text>
        </Pressable>
      </ScrollView>

      <TaskEditorSheet
        visible={editing !== null}
        task={editing && editing.id ? editing : null}
        onClose={() => setEditing(null)}
        onSubmit={editing && editing.id ? updateTask : addTask}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  head: { flexDirection: 'row', alignItems: 'center' },
  name: { ...typography.h2, color: colors.textPrimary },
  meta: { ...typography.caption, color: colors.textMuted, marginTop: 2 },
  task: { gap: spacing.sm },
  taskTop: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  taskTitle: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 14, flex: 1 },
  taskTime: { ...typography.caption, color: colors.textSecondary, fontVariant: ['tabular-nums'] },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  taskActions: { flexDirection: 'row', gap: spacing.lg, marginTop: 2 },
  empty: { ...typography.body, color: colors.textMuted, textAlign: 'center', paddingVertical: spacing.lg },
  addTask: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.sm, borderWidth: 1, borderColor: colors.border, borderStyle: 'dashed', borderRadius: radius.md, paddingVertical: spacing.md, marginTop: spacing.xs },
  addPlus: { color: colors.primary, fontSize: 18, fontWeight: '700' },
  addText: { ...typography.button, fontSize: 14, color: colors.textSecondary },
  gone: { ...typography.body, color: colors.textMuted, textAlign: 'center', marginTop: spacing.xxl },
});
